import { Fragment } from "react";
import { fmt, minToHm } from "@/lib/units";
import { Radio } from "lucide-react";

const Cell = ({ value, onChange, tid, w = "w-12", color = "text-slate-300", step }) => (
  <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid}
    className={`${w} rounded bg-transparent ${color} focus:bg-slate-800 focus:outline-none`} />
);

const HEAD = ["Point", "Alt", "Rv°", "Décl", "Vent °/kt", "Dérive", "Cap vrai", "Cap mag", "Cap comp", "Dist", "Vsol", "Temps", "Cumul", "Carbu"];
const CYAN = ["Vent °/kt", "Cap vrai", "Cap mag"];

// Tableau éditable d'un log de nav. p : préfixe des data-testid ("" aller, "ret-" retour).
export default function NavLogTable({ title, nav, setLeg, p = "" }) {
  return (
    <div className="glass overflow-hidden rounded-2xl" data-testid={`${p}navlog-table`}>
      {title && <div className="border-b border-slate-800 px-5 py-3 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">{title}</div>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] border-collapse text-left font-mono-data text-xs">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-950/60 text-[10px] uppercase tracking-wider text-slate-400">
              {HEAD.map((h, i) => <th key={h} className={`${i ? "px-2" : "px-3"} py-2.5 ${CYAN.includes(h) ? "text-cyan-400" : ""}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {nav.rows.map((r, i) => (
              <Fragment key={i}>
                <tr data-testid={`${p}navlog-row-${i}`} className={i % 2 ? "bg-slate-900/40" : "bg-slate-900/70"}>
                  <td className="px-3 py-2 font-semibold text-slate-100">{r.name || `Pt ${i + 1}`}</td>
                  <td className="px-2 py-1.5"><Cell w="w-14" value={r.altitude} onChange={(v) => setLeg(i, "altitude", v)} tid={`${p}nav-alt-${i}`} /></td>
                  <td className="px-2 py-1.5"><Cell value={r.trackTrue} onChange={(v) => setLeg(i, "trackTrue", v)} tid={`${p}nav-track-${i}`} /></td>
                  <td className="px-2 py-1.5"><Cell step="0.1" value={r.variation} onChange={(v) => setLeg(i, "variation", v)} tid={`${p}nav-var-${i}`} /></td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1">
                      <Cell w="w-10" color="text-cyan-300" value={r.windDir} onChange={(v) => setLeg(i, "windDir", v)} tid={`${p}nav-winddir-${i}`} />
                      <span className="text-slate-600">/</span>
                      <Cell w="w-10" color="text-cyan-300" value={r.windSpeed} onChange={(v) => setLeg(i, "windSpeed", v)} tid={`${p}nav-windspd-${i}`} />
                    </div>
                  </td>
                  <td className="px-2 py-2 text-slate-300">{r.wca > 0 ? `+${r.wca}` : r.wca}°</td>
                  <td className="px-2 py-2 font-bold text-cyan-300">{fmt(r.headingTrue)}°</td>
                  <td className="px-2 py-2 font-bold text-cyan-300">{fmt(r.headingMag)}°</td>
                  <td className="px-2 py-2 text-slate-300">{fmt(r.headingCompass)}°</td>
                  <td className="px-2 py-2 text-slate-300">{fmt(r.distance)}</td>
                  <td className="px-2 py-2 text-slate-300">{fmt(r.gs)} kt</td>
                  <td className="px-2 py-2 text-slate-300">{fmt(r.timeMin)}′</td>
                  <td className="px-2 py-2 text-slate-400">{minToHm(r.cumTime)}</td>
                  <td className="px-2 py-2 text-slate-300">{fmt(r.fuel, 1)} L</td>
                </tr>
                {(r.frequencies || []).length > 0 && (
                  <tr className="bg-slate-950/40" data-testid={`${p}navlog-freq-row-${i}`}>
                    <td colSpan={14} className="px-3 py-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-cyan-500/70"><Radio className="h-3 w-3" /> Fréq.</span>
                        {r.frequencies.map((f, fi) => (
                          <span key={fi} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900/60 px-2 py-0.5 text-[11px] text-slate-200">
                            <span className="font-semibold text-cyan-300">{f.type}</span>
                            {f.name && <span className="text-slate-300">{f.name}</span>}
                            {f.freq && <span className="font-mono-data text-emerald-300">{f.freq}</span>}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-cyan-500/30 bg-cyan-500/5 font-bold text-cyan-200" data-testid={`${p}navlog-totals`}>
              <td className="px-3 py-2.5" colSpan={9}>TOTAUX</td>
              <td className="px-2 py-2.5">{fmt(nav.totalDistance)} NM</td>
              <td className="px-2 py-2.5"></td>
              <td className="px-2 py-2.5">{minToHm(nav.totalTime)}</td>
              <td className="px-2 py-2.5"></td>
              <td className="px-2 py-2.5">{fmt(nav.totalFuel, 1)} L</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
